import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useProject } from "@/hooks/useProject";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { ColorPicker } from "./ColorPicker";
import { ChartDataParams } from "./ChartDataParams";
import { RadialChartConfig } from "./RadialChartConfig";

interface ChartConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateId: number;
  chart?: any | null;
  jotformFormId?: string | null;
  jotformFormIdReferral?: string | null;
}

const CHART_TYPES = [
  { value: "bar", label: "Barras Horizontales" },
  { value: "pie", label: "Gráfico Circular (Pie)" },
  { value: "radial", label: "Gráfico Radial" },
  { value: "pie-only-color", label: "Pie Solo Color" },
  { value: "bar-referral", label: "Barras Comparativas (Usuario vs Referido)" },
  { value: "pie-update", label: "Pie Actualizado" },
];

// Conversión: 1 pulgada = 72 puntos
const POINTS_PER_INCH = 72;

const pointsToInches = (points: number): number => {
  return Math.round((points / POINTS_PER_INCH) * 100) / 100; // Round to 2 decimals
};

const inchesToPoints = (inches: number): number => {
  return Math.round(inches * POINTS_PER_INCH);
};

export function ChartConfigDialog({ open, onOpenChange, templateId, chart, jotformFormId, jotformFormIdReferral }: ChartConfigDialogProps) {
  const { projectClient } = useProject();
  const queryClient = useQueryClient();
  const isEditing = !!chart;

  // Form state - Valores en pulgadas para la UI
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [slideNumber, setSlideNumber] = useState(1);
  const [positionX, setPositionX] = useState(1.39); // ~100 puntos en pulgadas
  const [positionY, setPositionY] = useState(1.39);
  const [width, setWidth] = useState(5.56);          // ~400 puntos en pulgadas
  const [height, setHeight] = useState(4.17);        // ~300 puntos en pulgadas
  const [chartType, setChartType] = useState<string>("bar");
  const [colors, setColors] = useState<string[]>([]);
  const [dataParams, setDataParams] = useState<any[]>([]);
  const [labels, setLabels] = useState<Record<string, string>>({});

  const upgradeCalculationValue = (value: any) => {
    if (value && typeof value === "object" && "type" in value && value.type === "calculation") {
      const raw = value.value;

      if (raw && typeof raw === "object" && "id" in raw) {
        return value;
      }

      let parsedId: number | null = null;

      if (typeof raw === "string" && raw.trim().length > 0) {
        const num = Number(raw);
        parsedId = Number.isFinite(num) ? num : null;
      } else if (typeof raw === "number" && Number.isFinite(raw)) {
        parsedId = raw;
      }

      return {
        ...value,
        value: {
          source: "calculation",
          id: parsedId,
        },
      };
    }

    return value;
  };

  const upgradeRadialDataParams = (params: any[]) => {
    return params.map((category) => ({
      ...category,
      dimensions: Array.isArray(category?.dimensions)
        ? category.dimensions.map((dimension: any) => ({
            ...dimension,
            barValue: upgradeCalculationValue(dimension?.barValue),
            lineValue: upgradeCalculationValue(dimension?.lineValue),
          }))
        : category.dimensions,
    }));
  };

  // Load chart data when editing
  useEffect(() => {
    if (chart) {
      setName(chart.name || "");
      setDescription(chart.description || "");
      setSlideNumber(chart.slide_number || 1);
      // Convertir puntos de la DB a pulgadas para la UI
      setPositionX(pointsToInches(chart.position_x || 100));
      setPositionY(pointsToInches(chart.position_y || 100));
      setWidth(pointsToInches(chart.width || 400));
      setHeight(pointsToInches(chart.height || 300));
      setChartType(chart.chart_type || "bar");

      const config = chart.chart_config || {};
      setColors(config.colors || []);

      // Normalize data_params for radial charts (old format -> new format)
      let normalizedDataParams = config.data_params || [];
      if (chart.chart_type === "radial" && normalizedDataParams.length > 0) {
        // Check if it's old format: [{label, value, color}]
        if (normalizedDataParams[0]?.label !== undefined && !normalizedDataParams[0]?.name) {
          // Convert old format to new format
          normalizedDataParams = normalizedDataParams.map((item: any) => ({
            name: item.label || "",
            color: item.color || "#000000",
            dimensions: [
              {
                name: "Valor",
                barValue: { type: "number", value: String(item.value || "") },
                lineValue: { type: "number", value: String(item.value || "") },
              }
            ]
          }));
        }
      }

      if (chart.chart_type === "radial") {
        normalizedDataParams = upgradeRadialDataParams(normalizedDataParams);
      }

      setDataParams(normalizedDataParams);
      setLabels(config.labels || {});
    } else {
      // Reset form - valores por defecto en pulgadas
      setName("");
      setDescription("");
      setSlideNumber(1);
      setPositionX(1.39);  // ~100 puntos
      setPositionY(1.39);  // ~100 puntos
      setWidth(5.56);      // ~400 puntos
      setHeight(4.17);     // ~300 puntos
      setChartType("bar");
      setColors([]);
      setDataParams([]);
      setLabels({});
    }
  }, [chart]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      const upgradedDataParams =
        chartType === "radial" ? upgradeRadialDataParams(dataParams) : dataParams;

      const chartConfig = {
        colors,
        data_params: upgradedDataParams,
        labels,
      };

      // Convertir pulgadas de la UI a puntos para la DB
      const chartData = {
        template_id: templateId,
        project_id: projectClient.getProjectId(),
        name,
        description,
        slide_number: slideNumber,
        position_x: inchesToPoints(positionX),
        position_y: inchesToPoints(positionY),
        width: inchesToPoints(width),
        height: inchesToPoints(height),
        chart_type: chartType,
        chart_config: chartConfig,
      };

      if (isEditing) {
        const { error } = await projectClient
          .from("template_charts")
          .update(chartData)
          .eq("id", chart.id)
          .eq("project_id", projectClient.getProjectId());

        if (error) throw error;
      } else {
        const { error } = await projectClient
          .from("template_charts")
          .insert(chartData);

        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success(isEditing ? "Gráfico actualizado" : "Gráfico creado");
      queryClient.invalidateQueries({ queryKey: ["template_charts", templateId] });
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast.error(error.message || "Error al guardar gráfico");
    },
  });

  const handleSave = () => {
    if (!name.trim()) {
      toast.error("El nombre es requerido");
      return;
    }
    saveMutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] w-[1400px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Editar Gráfico" : "Nuevo Gráfico"}
          </DialogTitle>
          <DialogDescription>
            Configura las propiedades del gráfico que se insertará en la presentación
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-8 py-4">
          {/* LEFT COLUMN - Configuration */}
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Información Básica</h3>
              <div className="space-y-2">
                <Label htmlFor="name">Nombre del Gráfico *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ej: Gráfico de Roles"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Descripción</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Descripción opcional del gráfico"
                />
              </div>
            </div>

            {/* PowerPoint Position */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Posicionamiento en PowerPoint</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="slide">Número de Slide</Label>
                  <Input
                    id="slide"
                    type="number"
                    min="1"
                    value={slideNumber}
                    onChange={(e) => setSlideNumber(parseInt(e.target.value) || 1)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unidades: Pulgadas (in)</Label>
                  <p className="text-xs text-muted-foreground">
                    1 pulgada = 72 puntos • Se convierten automáticamente
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="posX">Posición X (pulgadas)</Label>
                  <Input
                    id="posX"
                    type="number"
                    min="0"
                    step="0.01"
                    value={positionX}
                    onChange={(e) => setPositionX(parseFloat(e.target.value) || 0)}
                    placeholder="Ej: 1.5"
                  />
                  <p className="text-xs text-muted-foreground">
                    ≈ {inchesToPoints(positionX)} puntos
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="posY">Posición Y (pulgadas)</Label>
                  <Input
                    id="posY"
                    type="number"
                    min="0"
                    step="0.01"
                    value={positionY}
                    onChange={(e) => setPositionY(parseFloat(e.target.value) || 0)}
                    placeholder="Ej: 2.0"
                  />
                  <p className="text-xs text-muted-foreground">
                    ≈ {inchesToPoints(positionY)} puntos
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="width">Ancho (pulgadas)</Label>
                  <Input
                    id="width"
                    type="number"
                    min="0.1"
                    step="0.01"
                    value={width}
                    onChange={(e) => setWidth(parseFloat(e.target.value) || 0.1)}
                    placeholder="Ej: 5.5"
                  />
                  <p className="text-xs text-muted-foreground">
                    ≈ {inchesToPoints(width)} puntos
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="height">Alto (pulgadas)</Label>
                  <Input
                    id="height"
                    type="number"
                    min="0.1"
                    step="0.01"
                    value={height}
                    onChange={(e) => setHeight(parseFloat(e.target.value) || 0.1)}
                    placeholder="Ej: 4.0"
                  />
                  <p className="text-xs text-muted-foreground">
                    ≈ {inchesToPoints(height)} puntos
                  </p>
                </div>
              </div>
            </div>

            {/* Chart Type */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Tipo de Gráfico</h3>
              <Select value={chartType} onValueChange={setChartType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CHART_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Colors - Only show for non-radial charts */}
            {chartType !== "radial" && (
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Colores</h3>
                <ColorPicker
                  chartType={chartType}
                  colors={colors}
                  onChange={setColors}
                />
              </div>
            )}
          </div>

          {/* RIGHT COLUMN - Data Parameters */}
          <div className="space-y-6 border-l pl-8">
            {/* Data Parameters */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Parámetros de Datos</h3>
              {chartType === "radial" ? (
                <RadialChartConfig
                  data={dataParams}
                  onChange={setDataParams}
                  templateId={templateId}
                  jotformFormId={jotformFormId}
                  jotformFormIdReferral={jotformFormIdReferral}
                />
              ) : (
                <ChartDataParams
                  chartType={chartType}
                  dataParams={dataParams}
                  onChange={setDataParams}
                  labels={labels}
                  onLabelsChange={setLabels}
                  jotformFormId={jotformFormId}
                  jotformFormIdReferral={jotformFormIdReferral}
                  templateId={templateId}
                />
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saveMutation.isPending}>
            {saveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditing ? "Actualizar" : "Crear"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
